import { useRouter } from 'next/router';
// Types
import { InputActionMeta } from 'react-select';
import { SearchResultType } from 'api/graphql-analytics-global-types';
// Constants
import { SEARCH } from 'constants/routes';
// Hooks
// Context
import { useSearchBarContext } from 'components/HomePage/components/V3/SearchBar/hook/useSearchBarContext';
// Ui3
import Select from 'ui3/Select/Select';
// Components
import SearchOption from './SearchOption/SearchOption';
import SearchGroupHeading from './SearchGroupHeading/SearchGroupHeading';

export type SearchBarOptionProps = {
  name: string;
  searchTerm: string;
  value: string;
  label: string;
  url: string;
  id: string;
  type: SearchResultType;
};

export type SearchBarGroupProps = {
  label: string;
  options: SearchBarOptionProps[];
};

export type FilterOptionOption = {
  label: string;
  value: string;
  data: SearchBarOptionProps;
};

const HeaderSearchBar = () => {
  const { push } = useRouter();

  const {
    searchValue,
    setSearchValue,
    options,
    loading,
    setSearchFocused,
    setSelectedOption,
  } = useSearchBarContext();

  const optionValue = searchValue
    ? {
        label: searchValue,
        value: searchValue,
      }
    : null;

  const onInputChange = (value: string, action: InputActionMeta) => {
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      setSearchValue(value);

      return value;
    }

    if (action.action === 'input-blur') {
      setSearchValue(searchValue);

      return searchValue;
    }
  };

  const onSearchApply = (option: SearchBarOptionProps) => {
    // checking if option exists-in case of clear, it will be null, otherwise it will be an object
    if (option) {
      // send analytics when user selects an autocomplete option along with relevant data (search type and id)
      setSelectedOption(option);
    }
    setSearchValue(option.searchTerm);
    push(option.url);

    return option;
  };

  const navigateToSearchPage = () => {
    if (searchValue.trim()) {
      push(`${SEARCH}?q=${encodeURIComponent(searchValue)}`);
    } else {
      push(`${SEARCH}?showTop=true`);
    }
  };

  const handleSearchIconClick = () => {
    setSearchFocused(false);
    navigateToSearchPage();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // setting focused false will trigger the analytics api without the timer
      setSearchFocused(false);

      navigateToSearchPage();
    }
  };

  const commonSelectProps = {
    placeholder: 'Search Athletes, Sports, Streams and more...',
    defaultOptions: true,
    getOptionLabel: (option: SearchBarOptionProps) => option.label,
    getOptionValue: (option: SearchBarOptionProps) => option.value,
    options,
    isLoading: loading,
    onInputChange,
    onChange: onSearchApply,
    onKeyDown,
    value: optionValue,
    defaultValue: optionValue,
    inputValue: searchValue,
    defaultInputValue: searchValue,
    cacheOptions: true,
    dropdownIndicator: 'search',
    dropdownIndicatorPosition: 'right',
  } as const;

  return (
    <div style={{ position: 'relative' }}>
      <Select
        {...commonSelectProps}
        id="homepageSearch"
        inputId="homepageSearchInput"
        size="medium"
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        components={{
          Option: SearchOption,
          GroupHeading: SearchGroupHeading,
        }}
        styles={{
          menuList: (provided) => ({
            ...provided,
            padding: '8px 16px',
            maxHeight: '610px',
          }),
          option: (provided, state) => ({
            ...provided,
            padding: '4px 0',
            backgroundColor: state.isFocused ? '#202127' : 'transparent',
            cursor: 'pointer',
          }),
          groupHeading: (provided) => ({
            ...provided,
            padding: '4px 0',
            marginBottom: 0,
          }),
        }}
      />
      <button
        type="button"
        onClick={handleSearchIconClick}
        aria-label="Search"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 40,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      />
    </div>
  );
};

export default HeaderSearchBar;
